import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

// ─── Types ───────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  user_id: string;
  discord_id: string;
  discord_username: string | null;
  discord_avatar: string | null;
  game_uuid: string | null;
  game_username: string | null;
  game_avatar_url: string | null;
  is_verified: boolean;
  verified_at: string | null;
  auth_code: string | null;
  auth_code_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

interface VerificationResult {
  auth_code: string;
  game_uuid: string;
  game_username: string;
  game_avatar_url: string | null;
  expires_at: string;
  is_bedrock: boolean;
}

interface PendingVerification {
  auth_code: string;
  game_uuid: string;
  game_username: string;
  is_bedrock: boolean;
  expires_at: string;
  check_attempts: number;
  max_check_attempts: number;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isProfileLoading: boolean;
  isAuthenticated: boolean;
  isVerified: boolean;
  signInWithDiscord: (scopes?: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  generateVerificationCode: (gameUsername: string) => Promise<{ data: VerificationResult | null; error: string | null }>;
  checkVerification: () => Promise<{ verified: boolean; error: string | null; attempts_remaining?: number }>;
  cancelVerification: () => Promise<{ success: boolean; error: string | null }>;
  getPendingVerification: () => Promise<{ pending: PendingVerification | null; expired?: boolean; error: string | null }>;
  refreshProfile: () => Promise<void>;
  checkDiscordGuildMembership: () => Promise<{ isMember: boolean; inviteUrl?: string }>;
  joinDiscordGuild: () => Promise<{ joined: boolean; alreadyMember: boolean; inviteUrl?: string }>;
  saveDiscordPermissions: (permissions: { betaFeatures: boolean; dmPush: boolean; grantedScopes: string[] }) => Promise<{ success: boolean; error: string | null }>;
  getDiscordPermissions: () => Promise<{ permissions: any; error: string | null }>;
}

// ─── Context ─────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  // ── Profile fetch (stable reference) ──
  const fetchProfile = useCallback(async () => {
    setIsProfileLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("auth-verification", {
        body: { action: "get_profile" },
      });
      if (error) {
        console.error("[AuthProvider] Profile fetch error:", error);
        return;
      }
      setProfile(data.profile);
    } catch (e) {
      console.error("[AuthProvider] Profile fetch exception:", e);
    } finally {
      setIsProfileLoading(false);
    }
  }, []);

  // ── Session init + listener ──
  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!mounted) return;
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          // Defer to avoid Supabase deadlock
          setTimeout(() => {
            if (mounted) fetchProfile().catch(console.error);
          }, 0);
        } else {
          setProfile(null);
          setIsProfileLoading(false);
        }
      }
    );

    const initSession = async () => {
      try {
        const { data: { session: existingSession }, error } = await supabase.auth.getSession();

        if (error) {
          console.error("[AuthProvider] getSession error:", error);
          if (mounted) {
            setIsLoading(false);
            setIsProfileLoading(false);
          }
          return;
        }

        if (mounted) {
          setSession(existingSession);
          setUser(existingSession?.user ?? null);

          if (existingSession?.user) {
            await fetchProfile().catch(console.error);
          } else {
            setIsProfileLoading(false);
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error("[AuthProvider] Init error:", error);
        if (mounted) {
          setIsLoading(false);
          setIsProfileLoading(false);
        }
      }
    };

    initSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // ── Actions ──

  const signInWithDiscord = useCallback(async (scopes?: string) => {
    const redirectUrl = `${window.location.origin}/dash`;
    const oauthScopes = scopes || "identify email guilds guilds.join";

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "discord",
      options: { redirectTo: redirectUrl, scopes: oauthScopes },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) setProfile(null);
    return { error };
  }, []);

  const generateVerificationCode = useCallback(
    async (gameUsername: string): Promise<{ data: VerificationResult | null; error: string | null }> => {
      try {
        const { data, error } = await supabase.functions.invoke("auth-verification", {
          body: { action: "generate_code", game_username: gameUsername },
        });
        if (error) return { data: null, error: error.message };
        if (data.error) return { data: null, error: data.error };
        await fetchProfile();
        return { data, error: null };
      } catch (e) {
        return { data: null, error: String(e) };
      }
    },
    [fetchProfile]
  );

  const checkVerification = useCallback(async (): Promise<{ verified: boolean; error: string | null; attempts_remaining?: number }> => {
    try {
      const { data, error } = await supabase.functions.invoke("auth-verification", {
        body: { action: "check_verification" },
      });
      if (error) return { verified: false, error: error.message };
      if (data.error) return { verified: false, error: data.error };
      if (data.verified) await fetchProfile();
      return { verified: data.verified, error: null, attempts_remaining: data.attempts_remaining };
    } catch (e) {
      return { verified: false, error: String(e) };
    }
  }, [fetchProfile]);

  const getPendingVerification = useCallback(async (): Promise<{ pending: PendingVerification | null; expired?: boolean; error: string | null }> => {
    try {
      const { data, error } = await supabase.functions.invoke("auth-verification", {
        body: { action: "get_pending_verification" },
      });
      if (error) return { pending: null, error: error.message };
      return { pending: data.pending || null, expired: data.expired, error: null };
    } catch (e) {
      return { pending: null, error: String(e) };
    }
  }, []);

  const cancelVerification = useCallback(async (): Promise<{ success: boolean; error: string | null }> => {
    try {
      const { data, error } = await supabase.functions.invoke("auth-verification", {
        body: { action: "cancel_verification" },
      });
      if (error) return { success: false, error: error.message };
      if (data.error) return { success: false, error: data.error };
      await fetchProfile();
      return { success: true, error: null };
    } catch (e) {
      return { success: false, error: String(e) };
    }
  }, [fetchProfile]);

  const checkDiscordGuildMembership = useCallback(async (): Promise<{ isMember: boolean; inviteUrl?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke("discord-guild", {
        body: { action: "check_membership" },
      });
      if (error) {
        console.error("Guild check error:", error);
        return { isMember: false };
      }
      return {
        isMember: data.isMember || false,
        inviteUrl: data.isMember ? undefined : "https://discord.gg/AyAHDF4ykd",
      };
    } catch (e) {
      console.error("Guild check error:", e);
      return { isMember: false };
    }
  }, []);

  const joinDiscordGuild = useCallback(async (): Promise<{ joined: boolean; alreadyMember: boolean; inviteUrl?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke("discord-guild", {
        body: { action: "join_guild" },
      });
      if (error) {
        console.error("Guild join error:", error);
        return { joined: false, alreadyMember: false, inviteUrl: "https://discord.gg/AyAHDF4ykd" };
      }
      return {
        joined: data.joined || false,
        alreadyMember: data.alreadyMember || false,
        inviteUrl: data.inviteUrl,
      };
    } catch (e) {
      console.error("Guild join error:", e);
      return { joined: false, alreadyMember: false, inviteUrl: "https://discord.gg/AyAHDF4ykd" };
    }
  }, []);

  const saveDiscordPermissions = useCallback(
    async (permissions: { betaFeatures: boolean; dmPush: boolean; grantedScopes: string[] }): Promise<{ success: boolean; error: string | null }> => {
      try {
        const { error } = await supabase.functions.invoke("auth-verification", {
          body: { action: "save_permissions", permissions },
        });
        if (error) return { success: false, error: error.message };
        return { success: true, error: null };
      } catch (e) {
        return { success: false, error: String(e) };
      }
    },
    []
  );

  const getDiscordPermissions = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("auth-verification", {
        body: { action: "get_permissions" },
      });
      if (error) return { permissions: null, error: error.message };
      return { permissions: data.permissions, error: null };
    } catch (e) {
      return { permissions: null, error: String(e) };
    }
  }, []);

  // ── Memoized value ──
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      profile,
      isLoading,
      isProfileLoading,
      isAuthenticated: !!session,
      isVerified: profile?.is_verified ?? false,
      signInWithDiscord,
      signOut,
      generateVerificationCode,
      checkVerification,
      cancelVerification,
      getPendingVerification,
      refreshProfile: fetchProfile,
      checkDiscordGuildMembership,
      joinDiscordGuild,
      saveDiscordPermissions,
      getDiscordPermissions,
    }),
    [
      user,
      session,
      profile,
      isLoading,
      isProfileLoading,
      signInWithDiscord,
      signOut,
      generateVerificationCode,
      checkVerification,
      cancelVerification,
      getPendingVerification,
      fetchProfile,
      checkDiscordGuildMembership,
      joinDiscordGuild,
      saveDiscordPermissions,
      getDiscordPermissions,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ─── Hook ────────────────────────────────────────────────────────────

export const useAuthContext = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return ctx;
};
